import { Link, useLocation } from "react-router-dom";
import { GraduationCap, Store, Tv } from "lucide-react";
import { PublicCurrencyProvider, usePublicCurrency } from "@/contexts/PublicCurrency";
import GlobalAppHeader from "@/components/shared/GlobalAppHeader";
import GlobalAppFooter from "@/components/shared/GlobalAppFooter";

function CurrencySwitch({ dark }) {
  const { currency, setCurrency } = usePublicCurrency();
  const base = "px-2.5 py-1 text-xs font-bold transition-colors";
  const on = dark ? "bg-white text-black" : "bg-black text-white";
  const off = dark ? "text-white/70 hover:text-white" : "text-black/60 hover:text-black";
  return (
    <div className={`flex items-center border-2 ${dark ? "border-white/40" : "border-black"} rounded-full overflow-hidden`} data-testid="pub-currency-switch" title="Currency for subscription & partner plans">
      {["INR", "USD"].map(c => (
        <button key={c} onClick={() => setCurrency(c)} className={`${base} ${currency === c ? on : off}`} data-testid={`pub-ccy-${c}`}>{c === "INR" ? "₹ INR" : "$ USD"}</button>
      ))}
    </div>
  );
}

function HeaderBar({ dark }) {
  const loc = useLocation();
  const nav = [
    { to: "/store", label: "Store", icon: Store },
    { to: "/tv", label: "Ponderr TV", icon: Tv },
  ];
  const link = (active) => dark
    ? `${active ? "bg-white text-black" : "text-white/80 hover:bg-white/10"}`
    : `${active ? "bg-black text-white" : "text-black hover:bg-black/5"}`;
  return (
    <header className={`sticky top-0 z-30 border-b-2 ${dark ? "border-white/20 bg-[#0F1020]" : "border-black bg-white"}`}>
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="pub-logo">
          <div className={`w-9 h-9 grid place-items-center border-2 rounded-lg ${dark ? "border-white/40 bg-white/10 text-white" : "border-black bg-[#C4B5FD] shadow-[2px_2px_0_0_#000]"}`}>
            <GraduationCap size={18} strokeWidth={2.5} />
          </div>
          <span className={`display text-lg font-black ${dark ? "text-white" : ""}`}>Ponderr</span>
        </Link>
        <nav className="flex items-center gap-1">
          {nav.map(n => {
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`pub-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold border-2 ${dark ? "border-transparent" : "border-transparent"} ${link(active)}`}>
                <n.icon size={15} /> <span className="hidden sm:inline">{n.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <CurrencySwitch dark={dark} />
          <Link to="/login" className={`hidden sm:inline text-xs font-bold px-3 py-1.5 rounded-full border-2 ${dark ? "border-white/40 text-white hover:bg-white/10" : "border-black hover:bg-black/5"}`} data-testid="pub-login">Sign in</Link>
        </div>
      </div>
    </header>
  );
}

function FooterBar({ dark }) {
  return (
    <footer className={`mt-12 border-t-2 ${dark ? "border-white/20 bg-[#0B0C18] text-white/80" : "border-black bg-[#1A1030] text-white/80"}`}>
      <div className="max-w-6xl mx-auto px-4 py-10 grid sm:grid-cols-4 gap-8 text-sm">
        <div className="sm:col-span-2">
          <div className="flex items-center gap-2 text-white"><GraduationCap size={20} /><span className="display text-xl font-black">Ponderr</span></div>
          <p className="mt-2 max-w-xs text-white/60">The operating system for modern schools — ERP, LMS, local bookstore marketplace & creator streaming.</p>
        </div>
        <div>
          <div className="font-bold text-white mb-2">Marketplace</div>
          <ul className="space-y-1.5">
            <li><Link to="/store" className="hover:text-white">Schoolz Store</Link></li>
            <li><Link to="/vendor/onboard" className="hover:text-white">Sell with us</Link></li>
            <li><Link to="/tv" className="hover:text-white">Ponderr TV</Link></li>
            <li><Link to="/creator/onboard" className="hover:text-white">Become a Creator</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-bold text-white mb-2">Platform</div>
          <ul className="space-y-1.5">
            <li><Link to="/" className="hover:text-white">For Schools</Link></li>
            <li><Link to="/onboard" className="hover:text-white">Provision a School</Link></li>
            <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
          </ul>
        </div>
      </div>
      <div className={`border-t ${dark ? "border-white/10" : "border-white/10"} py-4 text-center text-xs text-white/50`}>
        © {new Date().getFullYear()} Ponderr Schoolz · Powered by rangeinfo
      </div>
    </footer>
  );
}

export default function PublicLayout({ theme = "light", children }) {
  const dark = theme === "dark";
  return (
    <PublicCurrencyProvider>
      <div className="min-h-screen flex flex-col pf-app max-w-full overflow-x-hidden">
        <GlobalAppHeader />
        <div className="flex-1">{children}</div>
        <GlobalAppFooter />
      </div>
    </PublicCurrencyProvider>
  );
}
